import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, BookOpen, Loader2 } from "lucide-react";
import { useState } from "react";
import { useProfile } from "@/hooks/useProfile";
import { useProfileInterests } from "@/hooks/useInterests";
import { useCourses, useCourseApplications } from "@/hooks/useCourses";
import { toast } from "@/hooks/use-toast";

export default function AlunoAplicarCurso() {
  const navigate = useNavigate();
  const { cursoId } = useParams();
  const { profile, isLoading: profileLoading } = useProfile();
  const { interests } = useProfileInterests();
  const { data: courses } = useCourses();
  const { applications, apply } = useCourseApplications();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Resolve slug "basico" to actual course UUID
  const resolvedCourseId = (() => {
    if (!cursoId) return undefined;
    // If it looks like a UUID, use directly
    if (cursoId.includes("-") && cursoId.length > 30) return cursoId;
    // Otherwise resolve by cycle name
    const course = courses?.find(c => c.cycle === cursoId || c.cycle === "basic" && cursoId === "basico");
    return course?.id;
  })();

  const gates = {
    bio: !!profile?.bio,
    interesses: interests.length > 0,
    objetivo: !!profile?.objective,
  };
  const gatesComplete = Object.values(gates).every(Boolean);
  const completedCount = Object.values(gates).filter(Boolean).length;

  // Check if already applied
  const alreadyApplied = applications.some(a => a.course_id === resolvedCourseId);

  const handleSubmit = async () => {
    if (!gatesComplete || !resolvedCourseId) return;
    setIsSubmitting(true);
    try {
      await apply.mutateAsync({ courseId: resolvedCourseId });
      setSubmitted(true);
    } catch (err: any) {
      if (err.message?.includes("duplicate")) {
        toast({ title: "Você já aplicou para este curso" });
      } else {
        toast({ title: "Erro ao aplicar", description: err.message, variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (profileLoading) {
    return <div className="page-container flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (submitted || alreadyApplied) {
    const currentApp = applications.find(a => a.course_id === resolvedCourseId);
    const isAccepted = currentApp?.status === "accepted";
    const isRejected = currentApp?.status === "rejected";

    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.6 }}
          className={`w-20 h-20 rounded-full ${isAccepted ? "bg-success/10" : isRejected ? "bg-destructive/10" : "bg-accent/10"} flex items-center justify-center mb-6`}>
          <CheckCircle2 className={`w-10 h-10 ${isAccepted ? "text-success" : isRejected ? "text-destructive" : "text-accent"}`} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center max-w-sm">
          {isAccepted ? (
            <>
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Você foi aceito! 🎉</h1>
              <p className="text-muted-foreground mb-2">Parabéns! Sua aplicação para o Ciclo Básico foi aprovada.</p>
              <p className="text-sm text-muted-foreground mb-8">Entraremos em contato em breve com os próximos passos para iniciar o curso.</p>
            </>
          ) : isRejected ? (
            <>
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Aplicação não aprovada</h1>
              <p className="text-muted-foreground mb-8">Infelizmente sua aplicação não foi aprovada desta vez. Continue evoluindo seu perfil!</p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Aplicação enviada!</h1>
              <p className="text-muted-foreground mb-2">Sua aplicação para o Ciclo Básico foi registrada.</p>
              <p className="text-sm text-muted-foreground mb-8">Status: <span className="text-accent font-medium">Em análise</span> — Entraremos em contato com você em breve.</p>
            </>
          )}
          <div className="space-y-3">
            <Button variant="hero" size="lg" className="w-full" onClick={() => navigate("/aluno")}>Ir para Home</Button>
            <Button variant="outline" size="lg" className="w-full" onClick={() => navigate("/aluno/vagas")}>Ver Vagas</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/aprender")}><ArrowLeft className="w-5 h-5" /></Button>
        <span className="text-sm text-muted-foreground">Aplicar para curso</span>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl p-5 shadow-card border border-border mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
            <BookOpen className="w-6 h-6 text-success" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-foreground">Ciclo Básico</h1>
            <p className="text-sm text-muted-foreground">Gestão de Produtos Digitais</p>
          </div>
        </div>
      </motion.div>

      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
        <h2 className="font-heading text-base font-semibold text-foreground mb-3">Pré-requisitos</h2>
        <p className="text-sm text-muted-foreground mb-4">Complete seu perfil básico para aplicar</p>
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          {Object.entries(gates).map(([key, value], i) => {
            const labels: Record<string, string> = { bio: "Bio preenchida", interesses: "Interesses definidos", objetivo: "Objetivo atual definido" };
            return (
              <div key={key} className={`flex items-center gap-3 p-4 ${i < Object.keys(gates).length - 1 ? "border-b border-border" : ""}`}>
                {value ? <CheckCircle2 className="w-5 h-5 text-success" /> : <Circle className="w-5 h-5 text-muted-foreground" />}
                <span className={`text-sm flex-1 ${value ? "text-foreground" : "text-muted-foreground"}`}>{labels[key]}</span>
                {!value && <Button size="sm" variant="ghost" onClick={() => navigate("/aluno/perfil/editar")}>Completar</Button>}
              </div>
            );
          })}
        </div>
        {!gatesComplete && (
          <div className="bg-accent/10 rounded-xl p-4 mt-4 border border-accent/20">
            <p className="text-sm font-medium text-foreground">Falta só isso! ({completedCount}/3)</p>
          </div>
        )}
      </motion.section>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="sticky bottom-20 bg-background pt-4">
        <Button variant="hero" size="xl" className="w-full" onClick={handleSubmit} disabled={!gatesComplete || isSubmitting}>
          {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Enviando...</>) : (<>Enviar Aplicação<ArrowRight className="w-5 h-5" /></>)}
        </Button>
        {!gatesComplete && <p className="text-center text-xs text-muted-foreground mt-3">Complete os pré-requisitos para continuar</p>}
      </motion.div>
    </div>
  );
}
